import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Award, Users, Phone as PhoneIcon } from 'lucide-react';
import Layout from '../components/Layout';
import { useSchemaOrg } from '../hooks/useSchemaOrg';
import { useCMS } from '../hooks/useCMS';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { getMediaUrl } from '../utils/mediaUrl';

const TeamPage = () => {
  const { get, loading } = useCMS('team');
  const { name: companyName, SITE_URL } = useSchemaOrg();

  const teamMembers = [
    { id: 1, name: get('team_member1_name', ''), role: get('team_member1_role', ''), image: get('team_member1_image', '') },
    { id: 2, name: get('team_member2_name', ''), role: get('team_member2_role', ''), image: get('team_member2_image', '') },
    { id: 3, name: get('team_member3_name', ''), role: get('team_member3_role', ''), image: get('team_member3_image', '') },
    { id: 4, name: get('team_member4_name', ''), role: get('team_member4_role', ''), image: get('team_member4_image', '') },
    { id: 5, name: get('team_member5_name', ''), role: get('team_member5_role', ''), image: get('team_member5_image', '') },
    { id: 6, name: get('team_member6_name', ''), role: get('team_member6_role', ''), image: get('team_member6_image', '') },
  ].filter(m => m.name);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": companyName,
    "url": SITE_URL,
    "employee": teamMembers.map(member => ({
      "@type": "Person",
      "name": member.name,
      "jobTitle": member.role,
    }))
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white text-xl">Laster team...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Vårt team | {companyName}</title>
        <meta name="description" content={`Møt teamet bak ${companyName}. Erfarne teknikere og fagfolk som sørger for kvalitet i alt vi gjør.`} />
        <link rel="canonical" href={`${SITE_URL}/team`} />
        <meta property="og:title" content={`Vårt team | ${companyName}`} />
        <meta property="og:description" content={`Møt teamet bak ${companyName} - erfarne teknikere og fagfolk.`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
        {/* Hero Section */}
        <div className="relative pt-32 pb-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(239,68,68,0.1),transparent_50%)]" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-red-600/20 text-red-400 border-red-600/50 mb-4 px-4 py-1">
                {get('team_badge', 'Vårt Team')}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                <span className="text-white">{get('team_title_part1', 'Møt Våre')}</span>{' '}
                <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
                  {get('team_title_part2', 'Eksperter')}
                </span>
              </h1>
              <p className="text-xl text-gray-300">
                {get('team_subtitle', 'Sertifiserte teknikere med årevis erfaring innen tungbil og tilhenger')}
              </p>
            </div>
          </div>
        </div>

        {/* Team Grid */}
        <div className="container mx-auto px-4 pb-20" data-testid="team-grid">
          {teamMembers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <Card
                  key={member.id}
                  className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700 hover:border-red-600/50 backdrop-blur-sm overflow-hidden group transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl hover:shadow-red-500/20"
                  data-testid={`team-member-${member.id}`}
                >
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={getMediaUrl(member.image) || member.image}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h2 className="text-2xl font-bold text-white mb-1">{member.name}</h2>
                      <p className="text-red-400 font-medium">{member.role}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Users className="w-20 h-20 text-gray-600 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Teamet vårt</h2>
              <p className="text-gray-400 max-w-md mx-auto mb-8">
                Vi er et dedikert team av erfarne mekanikere og teknikere.
                Kontakt oss for å møte teamet personlig.
              </p>
              <a
                href="/kontakt"
                className="inline-flex items-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                Kontakt oss
              </a>
            </div>
          )}
        </div>

        {/* Company Values */}
        <div className="container mx-auto px-4 pb-20">
          <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
              Våre verdier
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Kvalitet</h3>
                <p className="text-gray-400">
                  Vi leverer alltid arbeid av høyeste kvalitet, uten kompromisser.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Service</h3>
                <p className="text-gray-400">
                  Kundens behov står alltid i sentrum for alt vi gjør.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PhoneIcon className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Tilgjengelighet</h3>
                <p className="text-gray-400">
                  Vi er alltid tilgjengelige for spørsmål og hjelp.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TeamPage;
